import type { CdkDragDrop } from '@angular/cdk/drag-drop';
import { DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  output,
  inject,
  input,
  model,
} from '@angular/core';

import { Subject, takeUntil } from 'rxjs';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import {
  AddNewButtonComponent,
  DialogService,
  DialogWidth,
  DividerComponent,
  IconComponent,
} from '@core/user-interface-legacy';

import { HttpErrorHandlerService } from '../../common';
import { DynamicHelpers } from '../../dynamic/helpers';
import type { DynamicText } from '../../dynamic/types';
import { FieldMappingStoreService } from '../data/src/field-mapping/field-mapping.service';
import { FieldMappingFormFieldMappingComponent } from '../field-mapping-form-field-mapping/field-mapping-form-field-mapping.component';
import type { FieldMapping, FormInput, Source } from '../types';

@Component({
  selector: 'rti-field-mapping-form-field',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './field-mapping-form-field.component.html',
  styleUrls: ['./field-mapping-form-field.component.less'],
  imports: [
    AddNewButtonComponent,
    DividerComponent,
    DragDropModule,
    FieldMappingFormFieldMappingComponent,
    IconComponent,
    TranslatePipe,
  ],
})
export class FieldMappingFormFieldComponent {
  private readonly helpers = inject(DynamicHelpers);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly i18n = inject(I18nService);
  private readonly fieldMappingStore = inject(FieldMappingStoreService);
  private readonly httpErrorHandlerService = inject(HttpErrorHandlerService);
  protected readonly dialogService = inject(DialogService);

  public readonly formInput = model<FormInput>(undefined!);
  public readonly integrationName = input<string>(undefined!);
  public readonly integrationKind = input<string>(undefined!);
  public readonly sourcesLoading = input<boolean>(true);
  public readonly sources = input<Source[]>(undefined!);
  public readonly formConfigId = input<number>(undefined!);

  public formFieldChanged = output<FormInput>();

  public editable: boolean = false;
  public label: string;
  public ifElseLabel: string;

  private ngOnDestroy$ = new Subject<void>();

  public ngOnInit(): void {
    this.ifElseLabel = this.getIfElseLabel();
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public toggleEditable(): void {
    this.editable = !this.editable;

    this.cdRef.detectChanges();
  }

  public onDrop(event: CdkDragDrop<FieldMapping>): void {
    const previousOrder = [...this.formInput().fieldMappings];

    const formInput = this.formInput();
    moveItemInArray(formInput.fieldMappings, event.previousIndex, event.currentIndex);

    this.cdRef.detectChanges();

    const orderedIds = formInput.fieldMappings.map(fm => fm.id);

    this.fieldMappingStore
      .updateFieldMappingsPositions(this.integrationKind(), this.formConfigId(), {
        orderedIds,
        inputChecksum: formInput.checksum,
      })
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe({
        error: error => {
          this.httpErrorHandlerService.default(error);

          this.formInput().fieldMappings = previousOrder;

          this.cdRef.detectChanges();
        },
      });
  }

  public async openDialog(): Promise<void> {
    const { FieldMappingAddFieldMappingDialogComponent } =
      await import('../field-mapping-add-field-mapping-dialog/field-mapping-add-field-mapping-dialog.component');

    this.dialogService
      .openCustom(
        FieldMappingAddFieldMappingDialogComponent,
        { width: DialogWidth.SmallX },
        {
          title: this.getFieldName(),
          subtitle: this.i18n.translate('rti.field_mapping.settings.add.dialog.subtitle', {
            integrationName: this.integrationName(),
          }),
          isLoading: this.sourcesLoading(),
          integrationKind: this.integrationKind(),
          formConfigId: this.formConfigId(),
          formInput: this.formInput(),
          sources: this.sources(),
        },
      )
      .afterClosed.pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(fieldMappings => {
        if (fieldMappings) {
          this.formInput.update(value => ({
            ...value,
            fieldMappings: [...value.fieldMappings, ...fieldMappings],
          }));

          this.formFieldChanged.emit(this.formInput());

          this.cdRef.detectChanges();
        }
      });
  }

  public handleFieldMappingDeletion(id: number): void {
    this.formInput.update(value => ({
      ...value,
      fieldMappings: value.fieldMappings.filter(fm => fm.id !== id),
    }));

    this.formFieldChanged.emit(this.formInput());

    this.cdRef.detectChanges();
  }

  public trackById(index: number, { id }: FieldMapping): number {
    return id;
  }

  public trackByIndex(index: number): number {
    return index;
  }

  public showIfElseLabel(itemsCount: number, isFirst: boolean): boolean {
    return itemsCount > 1 && !isFirst;
  }

  public buildText(label: DynamicText): string {
    return this.helpers.buildText(label);
  }

  private getFieldName(): string {
    const formInput = this.formInput();
    if (!formInput.label.length) return '';

    return this.helpers.buildText(formInput.label.slice(-1)[0]);
  }

  private getIfElseLabel(): string {
    return this.i18n.translate('rti.field_mapping.settings.if_else');
  }
}
